import { useApiToast } from 'components/Toaster';
import { useEffect, useState } from 'react';
import { useGetPersonsMutation } from 'services/person/person.endpoint';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';
import { User } from 'services/auth/auth.types';
import { Person } from 'services/person/person.types';
import { useSelector } from 'react-redux';
import { getUser } from 'features/auth/auth.slice';

export default function usePersonsVM() {
    const user = useSelector(getUser);
    const [persons, setPersons] = useState<Array<Person>>([]);
    const [personsFiltered, setPersonsFiltered] = useState<Array<Person>>();
    const [users, setUsers] = useState<Array<User>>([]);
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<{
        departments: { label: string; value: string }[];
        statuses: { label: string; value: string }[];
        appvs: { label: string; value: string }[];
    }>({ departments: [], statuses: [], appvs: [{ label: user?.fullname || '', value: String(user?.id) || '' }] });
    const [filterIsOpen, setFilterIsOpen] = useState<boolean>(true);

    const [
        getPersons,
        {
            isLoading: isGetPersonsLoading,
            isError: isGetPersonsError,
            isSuccess: isGetPersonsSuccess,
            error: personsError,
            data: personsResponse
        }
    ] = useGetPersonsMutation();

    const [
        getAllUsers,
        {
            isLoading: isGetAllUsersLoading,
            isError: isGetAllUsersError,
            isSuccess: isGetAllUsersSuccess,
            error: getAllUsersError,
            data: getAllUsersData
        }
    ] = useGetAllUsersMutation();

    const { showApiToast, showErrorToast } = useApiToast();

    useEffect(() => {
        getPersons(undefined);
    }, []);

    useEffect(() => {
        getAllUsers(undefined);
    }, []);

    useEffect(() => {
        if (isGetPersonsLoading) return;
        if (isGetPersonsError && personsError) {
            showErrorToast(personsError, {
                message: 'Erreur de chargement des personnes'
            });
            return;
        }
        if (isGetPersonsSuccess && personsResponse) {
            setPersons(personsResponse.data);
            return;
        }
    }, [isGetPersonsLoading, isGetPersonsError, isGetPersonsSuccess, personsError, personsResponse]);

    useEffect(() => {
        if (isGetAllUsersLoading) return;
        if (isGetAllUsersError && getAllUsersError) {
            showErrorToast(getAllUsersError, {
                message: 'Erreur lors de la récupération des utilisateurs'
            });
            return;
        }
        if (isGetAllUsersSuccess && getAllUsersData) {
            setUsers(getAllUsersData.data);
            return;
        }
    }, [isGetAllUsersLoading, isGetAllUsersError, isGetAllUsersSuccess, getAllUsersError, getAllUsersData]);

    useEffect(() => {
        setPersonsFiltered(
            persons?.filter((person) => {
                const matchesSearch = person.fullname.toLowerCase().includes(search.toLowerCase());

                const matchesDepartment =
                    filter.departments.length === 0 ||
                    (person.departments &&
                        filter.departments.some((dept) =>
                            person.departments?.some((department) => department.toLowerCase() === dept.value.toLowerCase())
                        ));

                const matchesStatus =
                    filter.statuses.length === 0 ||
                    filter.statuses.some((status) => status.value.toLowerCase() === person.status.toLowerCase());

                const matchesResponsiblePerson =
                    filter.appvs.length === 0 ||
                    (person.responsible_person && filter.appvs.some((appv) => parseInt(appv.value, 10) === person.responsible_person?.id));

                return matchesSearch && matchesDepartment && matchesStatus && matchesResponsiblePerson;
            })
        );
    }, [search, persons, filter]);

    return {
        isGetPersonsLoading,
        persons,
        search,
        setSearch,
        filter,
        setFilter,
        filterIsOpen,
        setFilterIsOpen,
        users,
        personsFiltered
    };
}
